import React from 'react';
import { Typography, Container } from '@mui/material';
import PlanPricingCards from 'app/shared-components/account/PlanPricingCards';
import ROICalculator from '../shared-components/ROICalculator';
import CTASection from '../shared-components/CTASection';
import TrustBadges from '../shared-components/TrustBadges';
import { CheckCircleOutline, HelpOutline } from '@mui/icons-material';

function Pricing() {
  const faqItems = [
    {
      question: "What's included in the free account?",
      answer: "Create a free account with no credit card required. The free account is yours forever and includes core features to get started. Upgrade anytime to unlock advanced features and higher limits."
    },
    {
      question: "How does the free trial work?",
      answer: "Paid plan trials require a credit card to start, but you won't be charged until the 14-day trial period ends. Cancel anytime during the trial and you won't be billed."
    },
    {
      question: "Can I change plans later?",
      answer: "Yes, you can upgrade or downgrade at any time. Changes take effect immediately."
    },
    {
      question: "Is there a contract?",
      answer: "No contracts. All plans are month-to-month or annual with the option to cancel anytime."
    },
    {
      question: "Is HIPAA compliance included?",
      answer: "Yes, all plans include HIPAA compliance and a Business Associate Agreement (BAA) at no additional cost."
    },
    {
      question: "Do you offer enterprise discounts?",
      answer: "Yes, for practices with 10+ providers, contact our sales team for custom volume pricing."
    },
    {
      question: "What are the payment processing fees?",
      answer: "Payment processing includes both Stripe's standard fees (2.9% + $0.30 per transaction) and Halespring's processing fee (0.5% - 0.75% depending on your subscription level). The total percentage is included in the processing rate shown in your subscription plan details."
    }
  ];

  return (
      <div className="min-h-screen bg-white">
        {/* HERO SECTION - Centered & Tightened */}
        <section className="pt-24 pb-20 bg-gradient-to-b from-blue-50 to-white">
          <Container maxWidth="lg">
            <div className="text-center max-w-4xl mx-auto">
              <Typography
                  variant="h1"
                  align="center"
                  className="text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight"
              >
                Invest in Growth. <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Not Overhead.
              </span>
              </Typography>

              <div className="inline-block mb-6 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm tracking-wide uppercase">
                Simple, Transparent Pricing
              </div>

              <Typography
                  variant="h2"
                  align="center"
                  className="text-xl text-gray-600 mb-10 font-normal leading-relaxed max-w-2xl mx-auto"
              >
                Start for free. Upgrade as you scale. <br />
                All plans include HIPAA compliance and support.
              </Typography>

              <div className="flex items-center justify-center gap-8">
                <TrustBadges />
              </div>
            </div>
          </Container>
        </section>

        {/* ROI CALCULATOR SECTION - Header Centered */}
        <section className="py-24 bg-white border-t border-gray-100 shadow-inner">
          <Container maxWidth="lg">
            {/* OPTION A FIX: Centered Header to match Hero */}
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Calculate Your Potential Return
              </Typography>
              <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
                See how much time savings and potential revenue you gain with Halespring.
              </Typography>
            </div>

            <ROICalculator />
          </Container>
        </section>

        {/* PRICING TIERS SECTION - Header Centered */}
        <section className="py-24 bg-gray-50">
          <Container maxWidth="lg">
            {/* OPTION A FIX: Centered Header */}
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Choose the Perfect Plan
              </Typography>
              <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
                Whether you are a solo practitioner or a growing group, we have a plan for you.
              </Typography>
            </div>

            <PlanPricingCards type="provider" isPublic signUpURL="/provider-sign-up" />
          </Container>
        </section>

        {/* FAQ SECTION - Grid Layout & Centered Header */}
        <section className="py-24 bg-white">
          <Container maxWidth="lg">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </Typography>
              <Typography variant="body1" align="center" className="text-xl text-gray-600">
                Everything you need to know about billing and trials.
              </Typography>
            </div>

            {/* UPDATED: 2-Column Grid for better visual balance */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
              {faqItems.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:bg-blue-50 transition-colors duration-300">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 mt-1">
                        <HelpOutline className="text-blue-600" />
                      </div>
                      <div>
                        <Typography variant="h6" className="font-bold text-gray-900 mb-3 leading-snug">
                          {item.question}
                        </Typography>
                        <Typography variant="body1" className="text-gray-600 leading-relaxed">
                          {item.answer}
                        </Typography>
                      </div>
                    </div>
                  </div>
              ))}
            </div>
          </Container>
        </section>

        {/* FINAL CTA SECTION */}
        <CTASection
            headline="Get Started with Halespring"
            copy="Create your free account now. Upgrade to premium anytime with a 14-day trial."
            primaryCTA="Get Started Free"
            secondaryCTA="View Features"
            trustElements={[
              "Free account - no card needed",
              "Premium trial available",
              "HIPAA BAA included",
              "Cancel anytime"
            ]}
        />
      </div>
  );
}

export default Pricing;